import { useEffect, useMemo } from "react";
import { useSessionData } from "helpers";
import { useGetSystemBannersQuery } from "store/api/notificationsApi";
import { extractSystemBannersFromResponse, SYSTEM_BANNERS_POLL_MS } from "../systemBannerUtils";

export function useLayoutSystemBanners() {
  const { device_token, session_id } = useSessionData();
  const canPoll = !!device_token && !!session_id;

  const { data, isFetching, isLoading, isUninitialized, refetch } = useGetSystemBannersQuery(
    {
      device_token: device_token ?? "",
      session_id: session_id ?? "",
    },
    {
      skip: !canPoll,
      pollingInterval: SYSTEM_BANNERS_POLL_MS,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (!canPoll) return undefined;

    const handleSessionDataChanged = () => {
      void refetch();
    };

    window.addEventListener("sessionDataChanged", handleSessionDataChanged);
    return () => {
      window.removeEventListener("sessionDataChanged", handleSessionDataChanged);
    };
  }, [canPoll, refetch]);

  const banners = useMemo(() => extractSystemBannersFromResponse(data), [data]);

  const isBannersLoading = useMemo(
    () =>
      !canPoll ||
      isUninitialized ||
      isLoading ||
      (isFetching && banners.length === 0 && (data === null || data === undefined)),
    [banners.length, canPoll, data, isFetching, isLoading, isUninitialized]
  );

  return {
    banners,
    isLoading: isBannersLoading,
    hasSession: canPoll,
  };
}
